import { useState, useRef, useEffect } from "react";
import { CaretLeft, CaretRight, PlayCircle, X } from "@phosphor-icons/react";

/**
 * Image/video gallery carousel.
 * Items: array de strings (URLs base64 o http). Detecta vídeo por extensión o por data:video/.
 */
export default function GalleryCarousel({ items = [], testId = "gallery" }) {
  const [idx, setIdx] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const scrollerRef = useRef(null);

  const isVideo = (u) => /^data:video\//.test(u) || /\.(mp4|webm|mov)(\?|$)/i.test(u);
  const safe = (i) => (items.length ? (i + items.length) % items.length : 0);
  const goto = (i) => setIdx(safe(i));

  useEffect(() => {
    const el = scrollerRef.current?.querySelector(`[data-thumb-i="${idx}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
  }, [idx]);

  if (!items.length) return null;

  const item = items[idx];

  return (
    <div data-testid={testId}>
      {/* Main viewer */}
      <div className="relative bg-[var(--ag-midnight)] border border-[var(--ag-border)] rounded-md overflow-hidden aspect-video">
        {isVideo(item) ? (
          <video src={item} controls className="w-full h-full object-contain" data-testid={`${testId}-video-${idx}`} />
        ) : (
          <button onClick={() => setLightbox(true)} className="w-full h-full" data-testid={`${testId}-open-${idx}`}>
            <img src={item} alt={`Trabajo ${idx + 1}`} className="w-full h-full object-cover" />
          </button>
        )}
        {items.length > 1 && (
          <>
            <button
              onClick={() => goto(idx - 1)}
              className="absolute top-1/2 -translate-y-1/2 left-2 w-9 h-9 rounded-full bg-black/60 hover:bg-black grid place-items-center text-white"
              aria-label="Anterior" data-testid={`${testId}-prev`}>
              <CaretLeft size={16} weight="bold" />
            </button>
            <button
              onClick={() => goto(idx + 1)}
              className="absolute top-1/2 -translate-y-1/2 right-2 w-9 h-9 rounded-full bg-black/60 hover:bg-black grid place-items-center text-white"
              aria-label="Siguiente" data-testid={`${testId}-next`}>
              <CaretRight size={16} weight="bold" />
            </button>
          </>
        )}
        <div className="absolute bottom-2 right-2 text-[10px] tabular-nums px-2 py-0.5 rounded bg-black/60 text-white">
          {idx + 1} / {items.length}
        </div>
      </div>

      {/* Thumbnails */}
      {items.length > 1 && (
        <div ref={scrollerRef} className="flex gap-2 mt-3 overflow-x-auto scrollbar-thin pb-1">
          {items.map((u, i) => (
            <button
              key={i}
              data-thumb-i={i}
              onClick={() => goto(i)}
              className={`relative shrink-0 w-20 h-14 rounded-md overflow-hidden border-2 transition ${i === idx ? "border-[var(--ag-gold)]" : "border-transparent opacity-60 hover:opacity-100"}`}
              data-testid={`${testId}-thumb-${i}`}
            >
              {isVideo(u) ? (
                <div className="absolute inset-0 grid place-items-center bg-black text-white">
                  <PlayCircle size={22} weight="fill" />
                </div>
              ) : (
                <img src={u} alt="" className="w-full h-full object-cover" />
              )}
            </button>
          ))}
        </div>
      )}

      {/* Lightbox */}
      {lightbox && !isVideo(item) && (
        <div onClick={() => setLightbox(false)} className="fixed inset-0 z-50 bg-black/90 backdrop-blur p-4 flex items-center justify-center cursor-zoom-out" data-testid={`${testId}-lightbox`}>
          <button onClick={() => setLightbox(false)} className="absolute top-4 right-4 text-white/80 hover:text-white" aria-label="Cerrar">
            <X size={28} />
          </button>
          <img src={item} alt="" className="max-h-[90vh] max-w-[95vw] object-contain rounded" />
        </div>
      )}
    </div>
  );
}
